import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router";

import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import Tab from "ds/components/Tab";
import Box from "ds/components/Box";
import useTableSort from "components/Table/useTableSort";
import useTitle from "hooks/useTitle";
import useErrorHandle from "hooks/useErrorHandle";
import useUserGuideGroups from "shared/UserGuides/useUserGuideGroups";

import {
  LAUNCHPAD_TRACK_FILTERS_ORDER_SETTINGS_KEY,
  SORT_CONFIG,
  initialSortDirection,
  initialSortOption,
} from "./constants";
import { GET_LAUNCHPAD_TRACK_ONBOARDING_PROGRESS } from "./gql";
import { LaunchPadTrackUserProgress } from "./types";
import useLaunchPadTrackSortedUsers from "./useSortedUsers";
import LaunchPadTrackList from "./List";
import LaunchPadTrackMissionCompletionCards, {
  LaunchPadTrackMission,
} from "./MissionCompletionCards";
import LaunchPadTrackSkillChart from "./SkillChart";
import styles from "./styles.module.css";

const LaunchPadTrack = () => {
  useTitle("LaunchPad · Track");

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY) ?? "";

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: LAUNCHPAD_TRACK_FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const { data, loading, error } = useQuery(GET_LAUNCHPAD_TRACK_ONBOARDING_PROGRESS);

  const { userGuideGroups, loading: groupsLoading } = useUserGuideGroups();

  const usersProgress: LaunchPadTrackUserProgress[] = useMemo(
    () =>
      data?.onboardingProgress.map((user) => ({
        ...user,
        id: user.userLogin,
      })) ?? [],
    [data?.onboardingProgress]
  );

  const filteredUsersWithProgress = useMemo(() => {
    if (!searchQuery) return usersProgress;
    return usersProgress.filter((user) =>
      user.userLogin.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [usersProgress, searchQuery]);

  const sortedUsersWithProgress = useLaunchPadTrackSortedUsers(
    filteredUsersWithProgress,
    sortDescriptor
  );

  const missions: LaunchPadTrackMission[] = useMemo(() => {
    if (!userGuideGroups) return [];

    return userGuideGroups.map((group) => {
      const completedCount = usersProgress.filter((user) =>
        user.completedMissions?.some((mission) => mission.groupID === group.id)
      ).length;
      const inProgressCount = usersProgress.filter((user) =>
        user.inProgressMissions?.some((mission) => mission.groupID === group.id)
      ).length;

      return {
        id: group.id,
        label: group.name,
        icon: group.icon,
        iconColor: group.color,
        completedCount,
        startedCount: completedCount + inProgressCount,
      };
    });
  }, [userGuideGroups, usersProgress]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <ViewHeader>
        <ViewHeaderTitle>Welcome to LaunchPad</ViewHeaderTitle>
        <ViewHeaderNavigation>
          <Tab to="/launchpad" end label="Learn" />
          <Tab to="/launchpad/track" label="Track" />
        </ViewHeaderNavigation>
      </ViewHeader>
      <Box
        padding="x-large"
        grow="1"
        grid
        gridTemplate="5fr 2fr"
        gap="x-large"
        zeroMinWidth
        zeroMinHeight
      >
        <Box direction="column" zeroMinWidth gap="large" className={styles.wrapper}>
          <LaunchPadTrackMissionCompletionCards
            missions={missions}
            totalUsers={usersProgress.length}
            loading={loading || groupsLoading}
          />
          <LaunchPadTrackList
            usersWithProgress={sortedUsersWithProgress}
            loading={loading}
            searchQuery={searchQuery}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortOptionChange}
          />
        </Box>
        <Box direction="column" gap="x-large" zeroMinWidth>
          <LaunchPadTrackSkillChart
            missions={missions}
            totalUsers={usersProgress.length}
            loading={loading || groupsLoading}
          />
        </Box>
      </Box>
    </>
  );
};

export default LaunchPadTrack;
